// components/Updates.jsx
import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getPendingNotifications } from "../../../hooks/admin_api";
import API from "../../../services/base_url";
import "./Update.css";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import { format } from "date-fns";
import { useNotifications } from "../../../contexts/NotificationContext";
import { AuthContext } from "../../../contexts/AuthContext";

const Updates = () => {
  const { user, token } = useContext(AuthContext);
  const { refreshTrigger, failedSmsResults } = useNotifications(); // Assuming you added failedSmsResults to context
  const [activeTab, setActiveTab] = useState("notifications");
  const [notifications, setNotifications] = useState([]);
  const [smsLogs, setSmsLogs] = useState([]);
  const [smsStats, setSmsStats] = useState({
    total: 0,
    successful: 0,
    failed: 0,
  });
  const [loading, setLoading] = useState({ notifications: false, sms: false });
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [failedDetails, setFailedDetails] = useState([]);
  const [showFailedDialog, setShowFailedDialog] = useState(false);
  const [recentFailed, setRecentFailed] = useState([]);
  const [clearing, setClearing] = useState(false);
  const [deletingIds, setDeletingIds] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [logsToDelete, setLogsToDelete] = useState([]);
  const [deleteAllOlderThan, setDeleteAllOlderThan] = useState("7"); // days

  // Load notifications and SMS logs
  useEffect(() => {
    if (token) {
      loadNotifications();
      loadSMSLogs();
    }
  }, [token]);

  // Refresh when trigger changes
  useEffect(() => {
    if (token) {
      loadNotifications();
      loadSMSLogs();
    }
  }, [token, refreshTrigger]);

  // Show failed recipients dialog when new failed SMS results arrive
  useEffect(() => {
    if (failedSmsResults && failedSmsResults.failed > 0) {
      setRecentFailed(
        failedSmsResults.details?.filter((d) => d.status === "failed") || []
      );
      setShowFailedDialog(true);
      // Auto-switch to SMS tab
      setActiveTab("sms");
    }
  }, [failedSmsResults]);

  const loadNotifications = async () => {
    try {
      setLoading((prev) => ({ ...prev, notifications: true }));
      const res = await getPendingNotifications(token);

      if (res.status) {
        setNotifications(res.data || []);
      }
    } catch (err) {
      toast.error("Failed to load notifications");
    } finally {
      setLoading((prev) => ({ ...prev, notifications: false }));
    }
  };

  const loadSMSLogs = async () => {
    try {
      setLoading((prev) => ({ ...prev, sms: true }));
      console.log("📱 Fetching SMS logs...");

      const response = await API.get("/welfare/welfare-system/get_sms_logs", {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: 1, limit: 100 },
      });

      console.log("📱 SMS Logs API Response:", response.data);

      if (response.data.status) {
        const logsData = response.data.logs || [];
        console.log(`📱 Fetched ${logsData.length} logs`);

        // Log each log's status
        logsData.forEach((log, index) => {
          console.log(
            `Log ${index + 1}: ID=${log.id}, Status=${log.status}, User=${
              log.user_name
            }, Phone=${log.original_phone}`
          );
        });

        // Calculate statistics
        const successful = logsData.filter(
          (log) => log.status === "sent" || log.status === "success"
        ).length;

        const failed = logsData.filter(
          (log) => log.status === "failed" || log.status === "error"
        ).length;

        console.log(`📱 Stats - Successful: ${successful}, Failed: ${failed}`);

        setSmsLogs(logsData);
        setSmsStats({
          total: logsData.length,
          successful,
          failed,
        });
      } else {
        console.error("📱 API returned error:", response.data.message);
      }
    } catch (err) {
      console.error("📱 Error loading SMS logs:", err);
      console.error("📱 Error response:", err.response?.data);
    } finally {
      setLoading((prev) => ({ ...prev, sms: false }));
    }
  };

  const handleViewFailed = () => {
    console.log("🔍 Checking for failed logs...");
    console.log("Total logs:", smsLogs.length);
    console.log("All logs:", smsLogs);

    // More comprehensive status checking
    const failedLogs = smsLogs
      .filter((log) => {
        const status = (log.status || "").toLowerCase();
        const isFailed =
          status.includes("fail") ||
          status.includes("error") ||
          status === "failed" ||
          status === "error";
        console.log(`Log ${log.id}: status="${status}", isFailed=${isFailed}`);
        return isFailed;
      })
      .map((log) => {
        console.log("Processing failed log:", log);

        // Try to parse provider_response for error details
        let reason = "Unknown error";
        try {
          if (log.provider_response) {
            const response =
              typeof log.provider_response === "string"
                ? JSON.parse(log.provider_response)
                : log.provider_response;

            reason =
              response.error ||
              response.message ||
              response.reason ||
              JSON.stringify(response);
          }
        } catch (e) {
          reason = log.provider_response || "No error details";
        }

        return {
          id: log.id,
          user_name: log.user_name || log.full_name || "Unknown User",
          phone: log.original_phone || log.formatted_phone || "No phone",
          admin_name: log.admin_name || "Admin",
          reason: reason,
          time: log.created_at,
          message: log.message
            ? log.message.length > 50
              ? log.message.substring(0, 50) + "..."
              : log.message
            : "",
          raw_log: log, // Keep original for debugging
        };
      });

    console.log(`🔍 Found ${failedLogs.length} failed logs:`, failedLogs);

    if (failedLogs.length === 0) {
      // Show why no failed logs were found
      const allStatuses = smsLogs.map((l) => l.status).filter(Boolean);
      console.log("All statuses in logs:", allStatuses);

      // Check if maybe status field has different values
      const uniqueStatuses = [...new Set(allStatuses)];
      console.log("Unique status values:", uniqueStatuses);

      // Maybe show a helpful message
      toast.info(
        `No logs with status='failed' found. Statuses present: ${uniqueStatuses.join(
          ", "
        )}`
      );
    }

    setFailedDetails(failedLogs);
    setShowFailedModal(true);
  };

  // Delete individual log
  const handleDeleteLog = async (logId) => {
    if (!window.confirm("Are you sure you want to delete this log entry?"))
      return;

    try {
      setDeletingIds((prev) => [...prev, logId]);

      const response = await API.delete(
        `/welfare/welfare-system/sms_log/${logId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status) {
        toast.success("Log entry deleted");
        // Remove from local state
        setSmsLogs((prev) => prev.filter((log) => log.id !== logId));
        // Update stats
        setSmsStats((prev) => ({
          ...prev,
          total: prev.total - 1,
          failed: prev.failed - (response.data.was_failed ? 1 : 0),
          successful: prev.successful - (response.data.was_successful ? 1 : 0),
        }));
      } else {
        toast.error(response.data.message || "Failed to delete log");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete log");
    } finally {
      setDeletingIds((prev) => prev.filter((id) => id !== logId));
    }
  };

  // Delete multiple logs
  const handleDeleteMultiple = async (logIds) => {
    try {
      setDeletingIds((prev) => [...prev, ...logIds]);

      const response = await API.post(
        "/welfare/welfare-system/delete_sms_logs",
        {
          log_ids: logIds,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status) {
        toast.success(`Deleted ${response.data.deleted_count} log entries`);
        // Remove from local state
        setSmsLogs((prev) => prev.filter((log) => !logIds.includes(log.id)));
        // Update stats
        setSmsStats((prev) => ({
          ...prev,
          total: prev.total - response.data.deleted_count,
          failed: prev.failed - (response.data.failed_deleted || 0),
          successful: prev.successful - (response.data.successful_deleted || 0),
        }));
        setLogsToDelete([]);
        setShowDeleteModal(false);
      } else {
        toast.error(response.data.message || "Failed to delete logs");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete logs");
    } finally {
      setDeletingIds([]);
    }
  };

  // Clear logs older than X days
  const handleClearOldLogs = async () => {
    if (
      !window.confirm(
        `Clear SMS logs older than ${deleteAllOlderThan} days? This action cannot be undone.`
      )
    )
      return;

    try {
      setClearing(true);

      const response = await API.delete(
        `/welfare/welfare-system/clear_sms_logs`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { days: deleteAllOlderThan },
        }
      );

      if (response.data.status) {
        toast.success(`Cleared ${response.data.cleared_count} log entries`);
        loadSMSLogs(); // Refresh the list
      } else {
        toast.error(response.data.message || "Failed to clear logs");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to clear logs");
    } finally {
      setClearing(false);
    }
  };

  // Select/Deselect all logs for bulk delete
  const handleSelectAllLogs = () => {
    if (logsToDelete.length === smsLogs.length) {
      setLogsToDelete([]);
    } else {
      setLogsToDelete(smsLogs.map((log) => log.id));
    }
  };

  const handleToggleLogSelection = (logId) => {
    if (logsToDelete.includes(logId)) {
      setLogsToDelete((prev) => prev.filter((id) => id !== logId));
    } else {
      setLogsToDelete((prev) => [...prev, logId]);
    }
  };

  const formatTime = (timestamp) => {
    try {
      return format(new Date(timestamp), "MMM dd, h:mm a");
    } catch {
      return timestamp;
    }
  };

  return (
    <div className="Updates">
      {/* Tabs Header */}
      <div className="updates-header">
        <div className="tabs-container">
          <button
            className={`tab-button ${
              activeTab === "notifications" ? "active" : ""
            }`}
            onClick={() => setActiveTab("notifications")}
          >
            <i className="bi bi-bell me-1"></i>
            Notifications
            {notifications.length > 0 && (
              <Badge bg="danger" pill className="ms-2">
                {notifications.length}
              </Badge>
            )}
          </button>

          <button
            className={`tab-button ${activeTab === "sms" ? "active" : ""}`}
            onClick={() => setActiveTab("sms")}
          >
            <i className="bi bi-chat-text me-1"></i>
            SMS Activity
            {smsStats.failed > 0 && (
              <Badge bg="danger" pill className="ms-2">
                {smsStats.failed}
              </Badge>
            )}
          </button>
        </div>
      </div>

      {/* Notifications Tab */}
      {activeTab === "notifications" && (
        <div className="tab-content">
          <h5 className="updates-title">
            <i className="bi bi-bell me-2"></i>
            Pending Approvals
            {notifications.length > 0 && (
              <Badge bg="warning" className="ms-2">
                {notifications.length}
              </Badge>
            )}
          </h5>

          {loading.notifications ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
              <p className="text-muted mt-2 small">Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="no-notifications">
              <i className="bi bi-check-circle fs-3 text-success mb-2"></i>
              <p>All caught up! No pending notifications.</p>
            </div>
          ) : (
            <div className="updates-list">
              {notifications.map((update) => (
                <div className="update-item" key={update.contribution_id}>
                  <div className="update-avatar">
                    <span>{update.full_name?.[0]?.toUpperCase() || "U"}</span>
                  </div>
                  <div className="update-info">
                    <div className="update-text">
                      <span className="update-name">
                        {update.full_name || "Unknown User"}
                      </span>
                      <span className="update-action">
                        contributed ₵
                        {parseFloat(update.amount || 0).toLocaleString()}
                      </span>
                    </div>
                    <span className="update-time">
                      {formatTime(update.created_at)}
                    </span>
                  </div>
                  <span className="badge pending-badge">Pending</span>
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-3">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={loadNotifications}
              disabled={loading.notifications}
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Refresh
            </Button>
          </div>
        </div>
      )}

      {/* SMS Activity Tab */}
      {activeTab === "sms" && (
        <div className="tab-content">
          <div className="sms-stats-header">
            <h5 className="updates-title mb-2">
              <i className="bi bi-chat-text me-2"></i>
              SMS Activity
            </h5>

            {/* Stats Summary */}
            <div className="sms-stats-summary">
              <div className="stat-item">
                <div className="stat-number text-success">
                  {smsStats.successful}
                </div>
                <div className="stat-label">Successful</div>
              </div>
              <div className="stat-item">
                <div className="stat-number text-danger">{smsStats.failed}</div>
                <div className="stat-label">Failed</div>
              </div>
              <div className="stat-item">
                <div className="stat-number text-info">{smsStats.total}</div>
                <div className="stat-label">Total</div>
              </div>
            </div>
          </div>

          {loading.sms ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
              <p className="text-muted mt-2 small">Loading SMS logs...</p>
            </div>
          ) : smsLogs.length === 0 ? (
            <div className="no-notifications">
              <i className="bi bi-chat-dots fs-3 text-muted mb-2"></i>
              <p>No SMS activity yet</p>
              <small className="text-muted">
                Send your first bulk SMS to see activity here
              </small>
            </div>
          ) : (
            <>
              {/* Log Management Controls */}
              <div className="log-management mb-3 p-3 border rounded bg-light">
                <h6 className="mb-3">
                  <i className="bi bi-trash me-2"></i>
                  Log Management
                </h6>

                <div className="row g-2">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center mb-2">
                      <Form.Check
                        type="checkbox"
                        id="select-all-logs"
                        label="Select All"
                        checked={logsToDelete.length === smsLogs.length}
                        onChange={handleSelectAllLogs}
                        className="me-2"
                      />
                      <Badge bg="secondary" pill>
                        {logsToDelete.length} selected
                      </Badge>
                    </div>

                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() =>
                          logsToDelete.length > 0 && setShowDeleteModal(true)
                        }
                        disabled={logsToDelete.length === 0}
                      >
                        <i className="bi bi-trash me-1"></i>
                        Delete Selected ({logsToDelete.length})
                      </Button>

                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={handleClearOldLogs}
                        disabled={clearing}
                      >
                        {clearing ? (
                          <Spinner size="sm" animation="border" />
                        ) : (
                          <>
                            <i className="bi bi-calendar-x me-1"></i>
                            Clear Old Logs
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <Form.Label className="me-2 mb-0 small">
                        Clear logs older than:
                      </Form.Label>
                      <Form.Select
                        size="sm"
                        style={{ width: "80px" }}
                        value={deleteAllOlderThan}
                        onChange={(e) => setDeleteAllOlderThan(e.target.value)}
                      >
                        <option value="1">1 day</option>
                        <option value="3">3 days</option>
                        <option value="7">7 days</option>
                        <option value="30">30 days</option>
                        <option value="90">90 days</option>
                      </Form.Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="sms-actions mb-3">
                <div className="d-flex gap-2 flex-wrap">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleViewFailed}
                    disabled={smsStats.failed === 0}
                  >
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    View All Failed ({smsStats.failed})
                  </Button>

                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={loadSMSLogs}
                    disabled={loading.sms}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Refresh
                  </Button>

                  {/* <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => window.open("/admin/sms-logs", "_blank")}
                  >
                    <i className="bi bi-fullscreen me-1"></i>
                    Full View
                  </Button> */}
                </div>
              </div>

              {/* Quick Logs Table (Compact) */}
              {/* <div className="compact-logs-table">
                <h6 className="section-title">
                  <i className="bi bi-list-ul me-2"></i>
                  Recent Logs
                </h6>

                <div
                  className="table-responsive"
                  style={{ maxHeight: "300px", overflowY: "auto" }}
                >
                  <Table bordered size="sm" className="mb-0">
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: "white",
                        zIndex: 1,
                      }}
                    >
                      <tr>
                        <th width="30">
                          <Form.Check
                            type="checkbox"
                            checked={logsToDelete.length === smsLogs.length}
                            onChange={handleSelectAllLogs}
                          />
                        </th>
                        <th width="80">Status</th>
                        <th>User</th>
                        <th>Phone</th>
                        <th width="100">Time</th>
                        <th width="50">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {smsLogs.slice(0, 10).map((log) => (
                        <tr
                          key={log.id}
                          className={
                            deletingIds.includes(log.id) ? "opacity-50" : ""
                          }
                        >
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={logsToDelete.includes(log.id)}
                              onChange={() => handleToggleLogSelection(log.id)}
                              disabled={deletingIds.includes(log.id)}
                            />
                          </td>
                          <td>
                            <Badge
                              bg={log.status === "sent" ? "success" : "danger"}
                              pill
                              className="p-1"
                            >
                              <i
                                className={`bi bi-${
                                  log.status === "sent" ? "check" : "x"
                                }`}
                              ></i>
                            </Badge>
                          </td>
                          <td>
                            <div className="small">
                              <strong>{log.user_name || "Unknown"}</strong>
                              <div className="text-muted">
                                <small>{log.admin_name || "Admin"}</small>
                              </div>
                            </div>
                          </td>
                          <td className="small">{log.original_phone}</td>
                          <td className="small text-muted">
                            {formatTime(log.created_at)}
                          </td>
                          <td>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-danger p-0"
                              onClick={() => handleDeleteLog(log.id)}
                              disabled={deletingIds.includes(log.id)}
                              title="Delete this log"
                            >
                              {deletingIds.includes(log.id) ? (
                                <Spinner size="sm" animation="border" />
                              ) : (
                                <i className="bi bi-trash"></i>
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {smsLogs.length > 10 && (
                  <div className="text-center mt-2">
                    <small className="text-muted">
                      Showing 10 of {smsLogs.length} logs
                    </small>
                  </div>
                )}
              </div> */}
            </>
          )}
        </div>
      )}

      {/* Failed SMS Modal (Detailed View) */}
      <Modal
        show={showFailedModal}
        onHide={() => setShowFailedModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>
            <i className="bi bi-exclamation-triangle me-2"></i>
            Failed SMS Messages ({failedDetails.length})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {failedDetails.length === 0 ? (
            <div className="text-center py-4">
              <i className="bi bi-check-circle fs-1 text-success mb-3"></i>
              <h5>No Failed Messages</h5>
              <p className="text-muted">All SMS were sent successfully!</p>
            </div>
          ) : (
            <>
              <div className="alert alert-warning mb-3">
                <i className="bi bi-info-circle me-2"></i>
                These users did not receive the SMS. You may want to:
                <ul className="mb-0 mt-2">
                  <li>Check their phone numbers</li>
                  <li>Resend to failed recipients</li>
                </ul>
              </div>

              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th width="50">#</th>
                    <th>User Name</th>
                    <th>Phone Number</th>
                    <th>Error Reason</th>
                    <th>Time</th>
                    <th>Sent By</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {failedDetails.map((log, index) => (
                    <tr key={log.id} className="align-middle">
                      <td>{index + 1}</td>
                      <td>
                        <strong>{log.user_name}</strong>
                        {log.message && (
                          <div className="text-muted small mt-1">
                            <i className="bi bi-chat-left-text me-1"></i>
                            {log.message}
                          </div>
                        )}
                      </td>
                      <td>
                        <code>{log.phone}</code>
                        <div className="small text-muted">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0"
                            onClick={() =>
                              navigator.clipboard.writeText(log.phone)
                            }
                          >
                            <i className="bi bi-copy"></i> Copy
                          </Button>
                        </div>
                      </td>
                      <td className="text-danger">
                        <i className="bi bi-info-circle me-1"></i>
                        {log.reason}
                      </td>
                      <td className="small">{formatTime(log.time)}</td>
                      <td className="small">{log.admin_name}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              // Add resend functionality here
                              toast.info(`Resend SMS to ${log.user_name}`);
                            }}
                          >
                            <i className="bi bi-send"></i>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteLog(log.id)}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  <Button
                    variant="outline-danger"
                    onClick={() => {
                      const failedIds = failedDetails.map((f) => f.id);
                      handleDeleteMultiple(failedIds);
                      setShowFailedModal(false);
                    }}
                  >
                    <i className="bi bi-trash me-2"></i>
                    Delete All Failed Logs
                  </Button>
                </div>
                <div className="text-muted small">
                  Total failed messages: {failedDetails.length}
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFailedModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={loadSMSLogs}>
            <i className="bi bi-arrow-clockwise me-2"></i>
            Refresh Status
          </Button>
          <Button
            variant="warning"
            onClick={() => {
              // Export failed list functionality
              toast.info("Export feature coming soon");
            }}
          >
            <i className="bi bi-download me-2"></i>
            Export List
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Failed Recipients Dialog (After Sending SMS) */}
      <Modal
        show={showFailedDialog}
        onHide={() => setShowFailedDialog(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-warning">
          <Modal.Title>
            <i className="bi bi-exclamation-triangle me-2"></i>
            Some SMS Failed to Deliver
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning">
            <i className="bi bi-info-circle me-2"></i>
            The following recipients did not receive the SMS:
          </div>

          <div
            className="failed-recipients-list"
            style={{ maxHeight: "300px", overflowY: "auto" }}
          >
            {recentFailed.map((recipient, index) => (
              <div
                key={index}
                className="failed-recipient-item p-2 border-bottom"
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{recipient.name}</strong>
                    <div className="text-muted small">{recipient.phone}</div>
                  </div>
                  <Badge bg="danger" pill>
                    Failed
                  </Badge>
                </div>
                {recipient.reason && (
                  <div className="text-danger small mt-1">
                    <i className="bi bi-info-circle me-1"></i>
                    {recipient.reason}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-3">
            <h6>Recommended Actions:</h6>
            <ul>
              <li>Check phone numbers for incorrect format</li>
              <li>Verify network provider issues</li>
              <li>Consider resending to failed recipients</li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowFailedDialog(false)}
          >
            Dismiss
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowFailedDialog(false);
              setShowFailedModal(true);
            }}
          >
            View Full Details
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>
            <i className="bi bi-trash me-2"></i>
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            You are about to delete {logsToDelete.length} log entries.
            <br />
            <strong>This action cannot be undone.</strong>
          </div>
          <p>Are you sure you want to proceed?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => handleDeleteMultiple(logsToDelete)}
            disabled={deletingIds.length > 0}
          >
            {deletingIds.length > 0 ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <i className="bi bi-trash me-2"></i>
                Delete {logsToDelete.length} Logs
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Updates;
